import { useState } from "react";
import { Form, Modal, ModalProps } from "antd";
import { updateRecord } from "../../utils/slice";
import { validateFields } from "../../../../../config/Global";
import SVGIcon from "../../../../../utils/SVGIcon";
import Config from "../../../../../config/Config";
import CancelButton from "../../../../../components/Buttons/CancelButton";
import SaveButton from "../../../../../components/Buttons/SaveButton";
import FormComponent from "./FormComponent";
import { assignErrorToInput } from "../../../../../store/api";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../utils/hooks/hooks";

interface EditDrawerProps extends ModalProps {
  titleName: string;
  open: boolean;
  close: () => void;
}

/**
 * Renders a drawer component for editing a record in the database for EducationTraining module.
 *
 * @param {EditDrawerProps} props - The props object containing the following properties:
 *   - titleName: The name of the title for the drawer.
 *   - open: A boolean flag indicating whether the drawer is open.
 *   - close: A function to close the drawer.
 *   - rest: Any additional props to be spread onto the Modal component.
 * @return {JSX.Element} The rendered drawer component.
 */
const EditComponent: React.FC<EditDrawerProps> = ({
  titleName,
  open,
  close,
  ...rest
}) => {
  const dispatch = useAppDispatch();
  const details = useAppSelector(state => state.educationTraining.details);
  const [form] = Form.useForm();
  const [disabled, setDisabled] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const drawerClose = () => {
    close();
    form.resetFields();
    setDisabled(true);
  };

   /** 
   * Validates the form fields and sets the disabled state accordingly.
   */
  const validateForm = () => {
    validateFields(form, setDisabled);
  };

  /** 
   * Handles the form submission and dispatches the createRecord action.
   * @param {any} data - The form data.
   */
  const handleSubmit = (data: any) => {
    setSaving(true);
    dispatch(updateRecord(details.id, data))
      .then(() => {
        drawerClose();
      })
      .catch((error: any) => {
        assignErrorToInput(form, error?.STATUS);
      })
      .finally(() => setSaving(false));
  };

  return (
    <Modal
      title={`Edit ${titleName}`}
      open={open}
      width={"35%"}
      centered  
      onCancel={drawerClose}
      destroyOnClose
      closeIcon={<SVGIcon icon="close" color={Config.themePrimaryBtn} />}
      footer={[
        <CancelButton key={2} onClick={drawerClose} />,
        <SaveButton
          key={1}
          form="editForm"
          disabled={disabled}
          loading={saving}
        />,
      ]}
      {...rest}
    >
      <FormComponent
        form={form}
        id="editForm"
        editValues={details}
        handleSubmit={handleSubmit}
        onValuesChange={validateForm}
      />
    </Modal>
  );
};

export default EditComponent;
